import { useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-ssis-001': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20terracotta%20tones%2C%20soft%20geometric%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-ssis-001&orientation=landscape',
  'article-midv-001': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20soft%20peach%20tones%2C%20smooth%20geometric%20layers%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-midv-001&orientation=landscape',
  'article-top20': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20beige%20and%20coral%20tones%2C%20layered%20circular%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-top20&orientation=landscape',
  'article-ipx': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20sand%20tones%2C%20elegant%20flowing%20lines%2C%20clean%20negative%20space%2C%20soft%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-ipx&orientation=landscape',
  'article-search-tips': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20terracotta%20and%20ivory%20tones%2C%20angular%20geometric%20patterns%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-search-tips&orientation=landscape',
  'article-summer-new': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20peach%20and%20cream%20tones%2C%20organic%20curved%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-summer-new&orientation=landscape',
  'article-4k': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20coral%20and%20beige%20tones%2C%20sharp%20geometric%20diamonds%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-4k&orientation=landscape',
  'article-collection': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20sand%20and%20terracotta%20tones%2C%20grid%20based%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-collection&orientation=landscape',
  'article-abw': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20ivory%20and%20peach%20tones%2C%20concentric%20geometric%20rings%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-abw&orientation=landscape',
  'article-stars': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20editorial%20composition%20with%20warm%20cream%20and%20coral%20tones%2C%20starburst%20geometric%20shapes%2C%20clean%20negative%20space%2C%20elegant%20gradient%2C%20modern%20magazine%20style%2C%20warm%20atmosphere%2C%20no%20text%2C%20high%20quality&width=1200&height=600&seq=article-stars&orientation=landscape',
};

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const article = useMemo(
    () => articles.find((a) => a.id === Number(id)),
    [id]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="w-full">
          <div className="w-full px-4 md:px-6 py-10 bg-background-100 border-b border-background-200/60">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {t('navHome')}
                </Link>
                <span>/</span>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {article.categoryName}
                </Link>
                <span>/</span>
                <span className="text-foreground-700 line-clamp-1">{article.title}</span>
              </nav>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-xs bg-primary-500 text-background-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs"></i>
                  </div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </div>
                  {article.views.toLocaleString()} 次阅读
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </div>
                  阅读约 {article.readTime}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-3xl mx-auto">
              <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
                <img
                  src={articleImages[article.imageSeq]}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: article.content
                    .split('\n\n')
                    .map((block) => {
                      if (block.startsWith('## ')) {
                        return `<h2 class="text-xl md:text-2xl font-bold font-heading text-foreground-900 mt-10 mb-4">${block.replace('## ', '')}</h2>`;
                      }
                      if (block.startsWith('- ')) {
                        const items = block.split('\n').filter(Boolean);
                        if (items.every((item) => item.startsWith('- '))) {
                          return `<ul class="list-disc pl-5 space-y-2 my-4">${items.map((item) => `<li class="text-foreground-700">${item.replace('- ', '')}</li>`).join('')}</ul>`;
                        }
                      }
                      if (/^\d+\.\s/.test(block)) {
                        const items = block.split('\n').filter(Boolean);
                        if (items.every((item) => /^\d+\.\s/.test(item))) {
                          return `<ol class="list-decimal pl-5 space-y-2 my-4">${items.map((item) => `<li class="text-foreground-700">${item.replace(/^\d+\.\s/, '')}</li>`).join('')}</ol>`;
                        }
                      }
                      return `<p class="text-foreground-700 leading-relaxed mb-4">${block}</p>`;
                    })
                    .join('\n'),
                }}
              />
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="w-full px-4 md:px-6 py-12 bg-background-100 border-t border-background-200/60">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-lg p-4 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <h4 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{ra.date}</span>
                        <span>{ra.views.toLocaleString()} 阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Organization', name: '番号本' },
            publisher: { '@type': 'Organization', name: '番号本' },
          }),
        }} />
      </main>
      <Footer />
    </>
  );
}